import { useEffect, useMemo, useState } from "react";
import { AlertCircle, ChevronDown, RefreshCw } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { AIChatService } from "@/services/api/AIChatService";
import type { BrainChatMessage, ToolInvocation } from "@/types/brain/session";
import { CopyableJsonBlock } from "./CopyableJsonBlock";
import { MarkdownContent } from "./MarkdownContent";

interface IntermediateStep {
  content: string;
  iteration: number;
  tool_calls: Array<{ tool: string; rawTool: string; invocation_id?: string }>;
}

interface MessageDecisionTraceProps {
  message: BrainChatMessage;
  toolInvocationsById: Map<string, ToolInvocation>;
}

const getRoleBadge = (role: string) => {
  if (role === "user") {
    return (
      <Badge className="bg-genie-primary-light text-genie-primary border-genie-primary/20 border w-fit text-xs">
        User
      </Badge>
    );
  }
  return (
    <Badge className="bg-genie-background-muted text-genie-text-secondary border-genie-border border w-fit text-xs">
      Assistant
    </Badge>
  );
};

const ToolInvocationDetail = ({ invocation }: { invocation: ToolInvocation }) => (
  <div className="mt-2 bg-genie-background-muted rounded p-3 border border-genie-border space-y-3">
    <div className="flex items-center gap-2">
      <span className="text-xs font-semibold text-genie-text-primary font-mono">{invocation.tool_name}</span>
      <Badge
        className={cn(
          "border w-fit text-xs",
          invocation.status === "error"
            ? "bg-genie-error-light text-genie-error border-genie-error/20"
            : "bg-genie-primary-light text-genie-primary border-genie-primary/20",
        )}
      >
        {invocation.status}
      </Badge>
      {invocation.duration_ms !== null && (
        <span className="text-xs text-genie-text-muted">{invocation.duration_ms}ms</span>
      )}
      <span className="text-xs text-genie-text-muted ml-auto">{new Date(invocation.created_at).toLocaleString()}</span>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      <CopyableJsonBlock label="Request" value={invocation.request} />
      {invocation.response && <CopyableJsonBlock label="Response" value={invocation.response} />}
    </div>
    {invocation.error && <CopyableJsonBlock label="Error" value={invocation.error} tone="error" />}
  </div>
);

/** Auto-generates a 1-2 sentence explanation as soon as any tool call in this message failed. */
const ToolErrorSummary = ({ message, invocations }: { message: BrainChatMessage; invocations: ToolInvocation[] }) => {
  const [explanation, setExplanation] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const hasError = invocations.some((inv) => inv.status === "error");
  const invocationsKey = invocations.map((inv) => inv.id).join(",");

  useEffect(() => {
    if (!hasError) return;
    let cancelled = false;
    setIsLoading(true);
    setExplanation(null);

    AIChatService.explainToolErrors({
      toolCalls: invocations.map((inv) => ({
        toolName: inv.tool_name,
        request: inv.request,
        response: inv.response,
        status: inv.status,
        error: inv.error,
      })),
      assistantReply: message.content,
    })
      .then((result) => {
        if (!cancelled) setExplanation(result);
      })
      .catch(() => {
        if (!cancelled) setExplanation("Couldn't generate an explanation for this error.");
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasError, invocationsKey, message.content]);

  if (!hasError) return null;

  return (
    <div className="mt-2 flex items-start gap-2 rounded border border-genie-error/20 bg-genie-error-light px-3 py-2">
      <AlertCircle className="h-3.5 w-3.5 text-genie-error shrink-0 mt-0.5" />
      {isLoading ? (
        <span className="text-xs text-genie-error flex items-center gap-1.5">
          <RefreshCw className="h-3 w-3 animate-spin" />
          Analyzing what went wrong...
        </span>
      ) : (
        <span className="text-xs text-genie-error">{explanation}</span>
      )}
    </div>
  );
};

export const MessageDecisionTrace = ({ message, toolInvocationsById }: MessageDecisionTraceProps) => {
  const [expandedSteps, setExpandedSteps] = useState<Set<number>>(new Set());
  const [expandedTools, setExpandedTools] = useState<Set<string>>(new Set());

  const toggleStep = (index: number) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });
  };

  const toggleTool = (key: string) => {
    setExpandedTools((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const steps = (message.intermediate_messages as unknown as IntermediateStep[] | null) ?? [];
  const allStepsExpanded = steps.length > 0 && steps.every((_, stepIdx) => expandedSteps.has(stepIdx));

  const toggleAllSteps = () => {
    setExpandedSteps(allStepsExpanded ? new Set() : new Set(steps.map((_, stepIdx) => stepIdx)));
  };

  const messageInvocations = useMemo(() => {
    const invocations: ToolInvocation[] = [];
    for (const step of steps) {
      for (const tc of step.tool_calls ?? []) {
        const invocation = tc.invocation_id ? toolInvocationsById.get(tc.invocation_id) : undefined;
        if (invocation) invocations.push(invocation);
      }
    }
    return invocations;
  }, [steps, toolInvocationsById]);

  return (
    <div
      className={cn(
        "max-w-[85%] rounded-lg px-4 py-3 border",
        message.role === "user" ? "bg-genie-primary-light border-genie-primary/20" : "bg-white border-genie-border",
      )}
    >
      <div className="flex items-center gap-2 mb-1">
        {getRoleBadge(message.role)}
        <span className="text-xs text-genie-text-muted">{new Date(message.created_at).toLocaleTimeString()}</span>
      </div>
      <MarkdownContent content={message.content} />

      {messageInvocations.length > 0 && <ToolErrorSummary message={message} invocations={messageInvocations} />}

      {steps.length > 0 && (
        <div className="mt-3 space-y-2">
          <div className="flex justify-end">
            <button
              onClick={toggleAllSteps}
              className="text-xs text-genie-text-muted hover:text-genie-primary underline underline-offset-2"
            >
              {allStepsExpanded ? "Collapse all" : "Expand all"}
            </button>
          </div>
          {steps.map((step, stepIdx) => (
            <div key={stepIdx} className="border border-genie-border rounded">
              <button
                onClick={() => toggleStep(stepIdx)}
                className="w-full text-left p-2 flex items-center gap-2 hover:bg-genie-background-muted transition-colors"
              >
                <ChevronDown
                  className={cn("h-3.5 w-3.5 text-genie-text-muted transition-transform", !expandedSteps.has(stepIdx) && "-rotate-90")}
                />
                <span className="text-xs font-medium text-genie-text-secondary">
                  Iteration {step.iteration ?? stepIdx + 1}
                </span>
              </button>
              {expandedSteps.has(stepIdx) && (
                <div className="px-3 pb-3 space-y-2">
                  {step.content && <MarkdownContent content={step.content} className="text-xs text-genie-text-secondary" />}
                  <div className="flex flex-wrap gap-2">
                    {step.tool_calls?.map((tc, tcIdx) => {
                      const key = `${stepIdx}-${tcIdx}`;
                      const invocation = tc.invocation_id ? toolInvocationsById.get(tc.invocation_id) : undefined;
                      return (
                        <button
                          key={key}
                          onClick={() => invocation && toggleTool(key)}
                          disabled={!invocation}
                          className={cn(
                            "inline-flex",
                            !invocation && "cursor-not-allowed opacity-70",
                          )}
                        >
                          <Badge
                            className={cn(
                              "border text-xs font-mono transition-colors",
                              !invocation && "bg-genie-background text-genie-text-muted border-genie-border",
                              invocation?.status === "error" &&
                                "bg-genie-error-light text-genie-error border-genie-error/20 hover:bg-genie-error-light/80 cursor-pointer",
                              invocation &&
                                invocation.status !== "error" &&
                                "bg-genie-primary-light text-genie-primary border-genie-primary/20 hover:bg-genie-primary-light/80 cursor-pointer",
                            )}
                          >
                            {tc.tool}
                            {!invocation && " (unlinked)"}
                          </Badge>
                        </button>
                      );
                    })}
                  </div>
                  {step.tool_calls?.map((tc, tcIdx) => {
                    const key = `${stepIdx}-${tcIdx}`;
                    const invocation = tc.invocation_id ? toolInvocationsById.get(tc.invocation_id) : undefined;
                    return invocation && expandedTools.has(key) ? (
                      <ToolInvocationDetail key={key} invocation={invocation} />
                    ) : null;
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
